import { useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Download, X } from 'lucide-react';
import { useCustomDomain } from '@/hooks/useCustomDomain';

export const PWAPrompt = () => {
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isTimerActive, setIsTimerActive] = useState(false);
  const { isCustomDomain } = useCustomDomain();

  // Hide PWA prompt on ALL public academy landing pages (/a/:slug) AND custom domains
  const isPublicLanding = typeof window !== 'undefined' && (
    window.location.pathname.startsWith('/a/') || 
    window.location.pathname === '/a'
  );
  const shouldHide = isPublicLanding || isCustomDomain;

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, registration) {
      if (!registration) return;
      // Procura versão nova ao abrir e a cada 15 min — o app instalado
      // não pode ficar preso numa versão antiga do cronômetro.
      registration.update().catch(() => {});
      window.setInterval(() => registration.update().catch(() => {}), 15 * 60 * 1000);
    },
    onRegisterError(error) {
      console.log('SW registration error', error);
    },
  });

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  useEffect(() => {
    const updateTimerState = () => {
      setIsTimerActive(Boolean(document.querySelector('[data-timer-controls="immersive"]')));
    };

    updateTimerState();
    const observer = new MutationObserver(updateTimerState);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
      setShowInstallPrompt(false);
    }
  };

  // After all hooks, guard public routes and custom domains
  if (shouldHide) return null;

  return (
    <>
      {needRefresh && !isTimerActive && (
        <Card className="fixed bottom-4 right-4 left-4 md:left-auto z-[140] p-4 md:max-w-sm shadow-lg animate-in slide-in-from-bottom">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <h3 className="font-semibold mb-1">Nova versão disponível</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Toque em atualizar para pegar as melhorias mais recentes.
              </p>
              <div className="flex gap-2">
                <Button onClick={() => updateServiceWorker(true)} size="lg" className="h-12 text-base font-bold">
                  Atualizar
                </Button>
                <Button onClick={() => setNeedRefresh(false)} variant="outline" size="sm">
                  Depois
                </Button>
              </div>
            </div>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setNeedRefresh(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </Card>
      )}

      {showInstallPrompt && (
        <Card className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm z-50 p-4 shadow-lg animate-in slide-in-from-bottom">
          <div className="flex items-start gap-3">
            <Download className="h-5 w-5 text-primary mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold mb-1">Instalar FalaCoSagat</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Instale o app no seu dispositivo para acesso rápido e uso offline.
              </p>
              <div className="flex gap-2">
                <Button onClick={handleInstall} size="sm">Instalar</Button>
                <Button onClick={() => setShowInstallPrompt(false)} variant="outline" size="sm">Agora Não</Button>
              </div>
            </div>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setShowInstallPrompt(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </Card>
      )}
    </>
  );
};
